/**
 * Price Override Service
 * Fetches and manages station price overrides from the admin dashboard API
 * Used by the fuel app to display overridden prices to drivers
 */

// Cache for active overrides to avoid repeated API calls
let overridesCache: { [key: string]: any } = {};
let cacheFetchTime = 0;
const CACHE_TTL = 60000; // Cache for 1 minute

/**
 * Get the admin API base URL
 * In development, connects to localhost:3001
 * In production, connects to the same host
 */
function getAdminApiUrl(): string {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname.startsWith('127.0.0.1')) {
      return 'http://localhost:5000/api';
    }
    // In production, admin dashboard is at root.needfuel.net
    const protocol = window.location.protocol;
    if (hostname.includes('dev.needfuel.net')) {
      return `${protocol}//addev.needfuel.net/api`;
    }
    return `${protocol}//root.needfuel.net/api`;
  }
  return '/api';
}

/**
 * Fetch active price overrides from admin dashboard
 * Returns a map keyed by "stationCode:prodCode" for easy lookup
 */
export async function getActiveOverrides(
  stationCodes?: string[]
): Promise<{ [key: string]: any }> {
  // Check cache first
  const now = Date.now();
  if (cacheFetchTime && now - cacheFetchTime < CACHE_TTL && Object.keys(overridesCache).length > 0) {
    console.log('📦 Using cached price overrides');
    return overridesCache;
  }

  try {
    const apiUrl = getAdminApiUrl();
    const url = new URL(`${apiUrl}/price-overrides`);

    // Add station filter if provided
    if (stationCodes && stationCodes.length > 0) {
      url.searchParams.set('limit', '1000'); // Fetch up to 1000 for filtered stations
      url.searchParams.set('isActive', 'true');
    } else {
      url.searchParams.set('limit', '500');
      url.searchParams.set('isActive', 'true');
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 5000);
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    });
    window.clearTimeout(timeoutId);

    if (!response.ok) {
      console.warn(`⚠️ Failed to fetch price overrides: ${response.status}`);
      return overridesCache; // Return cached version on error
    }

    const data = await response.json();
    const overrides = data.overrides || [];

    // Build map for quick lookup
    const overrideMap: { [key: string]: any } = {};
    overrides.forEach((override: any) => {
      const key = `${override.stationCode}:${override.prodCode}`;
      overrideMap[key] = override;
    });

    // Update cache
    overridesCache = overrideMap;
    cacheFetchTime = now;

    console.log(`✅ Loaded ${overrides.length} active price overrides`);
    return overrideMap;
  } catch (error) {
    console.error('❌ Error fetching price overrides:', error);
    return overridesCache; // Return cached version on error
  }
}

/**
 * Get a specific override for a station and product
 */
export async function getOverride(stationCode: string, prodCode: string): Promise<any | null> {
  const overrides = await getActiveOverrides();
  const key = `${stationCode}:${prodCode}`;
  return overrides[key] || null;
}

/**
 * Clear the cache (useful for manual refresh)
 */
export function clearOverridesCache(): void {
  overridesCache = {};
  cacheFetchTime = 0;
  console.log('✅ Price overrides cache cleared');
}

/**
 * Get cache statistics
 */
export function getCacheStats(): {
  count: number;
  ageMs: number;
  ttlMs: number;
} {
  return {
    count: Object.keys(overridesCache).length,
    ageMs: cacheFetchTime ? Date.now() - cacheFetchTime : -1,
    ttlMs: CACHE_TTL,
  };
}
